import React, { useState, useRef, useContext } from 'react';
import styles from './ScreenShareModal.module.css';
import { IoDesktopOutline, IoClose, IoCamera, IoRecordingOutline } from 'react-icons/io5';
import { areImagesDifferent } from '../../services/imageSimilarity';
import { ScreenRecordingContext } from '../../contexts/ScreenRecordingContext';

const ScreenShareModal = ({ isOpen, onClose, onSubmit, embedded = false }) => {
  const [capturedImages, setCapturedImages] = useState([]);
  const canvasRef = useRef(null);
  const { startRecording, setIntervalRef } = useContext(ScreenRecordingContext);

  const handleCaptureWindow = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ 
        video: { 
          cursor: "always",
          displaySurface: "window"
        }
      });

      const videoTrack = stream.getVideoTracks()[0];

      // Create video element to capture frames
      const video = document.createElement('video');
      video.srcObject = stream;
      
      // Wait for video to load
      await new Promise(resolve => {
        video.onloadedmetadata = () => {
          video.play();
          resolve();
        };
      });

      // Setup canvas for capturing
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');

      // Capture frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);

      // Add to captured images
      setCapturedImages(prev => [...prev, imageData]);

      // Stop tracks
      stream.getTracks().forEach(track => track.stop());

    } catch (error) {
      if (error.name === 'NotAllowedError') {
        console.log('User denied screen share permission');
      } else {
        console.error('Error capturing screen:', error);
      }
    }
  };

  const captureFrame = async (video, canvas, ctx) => {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ 
        video: { 
          cursor: "always",
          displaySurface: "window"
        }
      });

      // Setup video and canvas
      const video = document.createElement('video');
      video.srcObject = stream;
      await new Promise(resolve => {
        video.onloadedmetadata = () => {
          video.play();
          resolve();
        };
      });

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');

      let lastImage = null;
      const captureInterval = setInterval(async () => {
        const currentImage = await captureFrame(video, canvas, ctx);
        
        // Only save if it's the first image or sufficiently different from the last
        if (!lastImage || await areImagesDifferent(currentImage, lastImage)) {
          startRecording(currentImage, stream);
          lastImage = currentImage;
        }
      }, 1000);

      // Store interval reference for cleanup
      setIntervalRef(captureInterval);

      // Handle stream ending
      stream.getVideoTracks()[0].onended = () => {
        clearInterval(captureInterval);
        stream.getTracks().forEach(track => track.stop());
      };

      // Close modal
      onClose();

    } catch (error) {
      console.error('Error starting screen recording:', error);
    }
  };

  const handleRemoveImage = (index) => {
    setCapturedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (capturedImages.length > 0) {
      onSubmit(capturedImages);
      setCapturedImages([]);
    }
  };

  if (!isOpen) return null;

  const content = (
    <>
      {!embedded && (
        <button className={styles.closeButton} onClick={onClose}>
          <IoClose size={24} />
        </button>
      )}
      
      {!embedded && <h3>Screen Share</h3>}
      
      <div className={styles.container}>
        <div className={styles.buttonGroup}>
          <button 
            className={styles.captureButton}
            onClick={handleCaptureWindow}
          >
            <IoCamera size={24} />
            <span>Capture Window</span>
          </button>
          <button 
            className={styles.captureButton}
            onClick={handleStartRecording}
          >
            <IoRecordingOutline size={24} />
            <span>Record Screen</span>
          </button>
        </div>

        {capturedImages.length > 0 && (
          <>
            <div className={styles.capturedImagesGrid}>
              {capturedImages.map((src, index) => (
                <div key={index} className={styles.capturedImageItem}>
                  <img 
                    src={src} 
                    alt={`Screenshot ${index + 1}`} 
                    className={styles.capturedImage}
                  />
                  <button
                    className={styles.removeButton}
                    onClick={() => handleRemoveImage(index)}
                    title="Remove screenshot"
                  >
                    <IoClose size={18} />
                  </button>
                </div>
              ))}
            </div>
            <div className={styles.actionButtons}>
              <button 
                className={styles.submitButton}
                onClick={handleSubmit}
              >
                Use {capturedImages.length} Screenshot{capturedImages.length > 1 ? 's' : ''}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Hidden canvas for capturing */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {content}
      </div>
    </div>
  );
};

export default ScreenShareModal;